import { useState, useEffect, useRef } from 'react';
import { tourConfig, labConfig, getTourConfig } from '../config/tourConfig';
import LabSelector from './LabSelector';
import Controls from './Controls';
import Modal from './Modal';

const VirtualTour = () => {
    const [currentLab, setCurrentLab] = useState('318');
    const [isLoading, setIsLoading] = useState(true);
    const [viewer, setViewer] = useState(null);
    const [showModal, setShowModal] = useState({ show: false, title: '', content: '' });
    const panoramaRef = useRef(null);

    // Функція для конвертації переносів рядків у HTML-параграфи
    const formatContent = (text) => {
        if (!text) return '';
        // Розділяємо текст на параграфи (подвійні переноси рядків)
        return text
            .split(/\n\n/)
            .map(paragraph => paragraph.trim())
            .filter(paragraph => paragraph.length > 0)
            .map(paragraph => `<p style="margin-bottom: 1em;">${paragraph.replace(/\n/g, '<br>')}</p>`)
            .join('');
    };

    // Встановлення правильної висоти viewport для мобільних пристроїв
    useEffect(() => {
        const setViewportHeight = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setViewportHeight();
        window.addEventListener('resize', setViewportHeight);
        window.addEventListener('orientationchange', setViewportHeight);

        return () => {
            window.removeEventListener('resize', setViewportHeight);
            window.removeEventListener('orientationchange', setViewportHeight);
        };
    }, []);

    // Отримуємо параметр lab з URL при завантаженні
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const labParam = urlParams.get('lab');
        if (labParam && labConfig[labParam]) {
            setCurrentLab(labParam);
        }
    }, []);

    // Ініціалізація Pannellum
    useEffect(() => {
        if (!window.pannellum) {
            console.error('Pannellum не завантажений');
            return;
        }

        const currentLabConfig = labConfig[currentLab];
        const startScene = currentLabConfig.startScene;

        // Отримуємо динамічну конфігурацію з правильним hfov для поточного пристрою
        const dynamicTourConfig = getTourConfig();
        const startSceneConfig = dynamicTourConfig.scenes[startScene];

        console.log('Використовується hfov:', startSceneConfig.hfov, 'для пристрою:', window.innerWidth <= 768 ? 'мобільний' : 'десктоп');

        if (!startSceneConfig) {
            console.error('Сцена не знайдена:', startScene);
            return;
        }

        // Обробка подій hotspots - додаємо clickHandlerFunc для info hotspots
        const configWithHandlers = { ...dynamicTourConfig };
        Object.keys(configWithHandlers.scenes).forEach((sceneKey) => {
            const scene = configWithHandlers.scenes[sceneKey];
            if (scene.hotSpots) {
                scene.hotSpots.forEach((hotspot) => {
                    if (hotspot.type === "info") {
                        hotspot.clickHandlerFunc = () => {
                            setShowModal({
                                show: true,
                                title: hotspot.title || 'Інформація',
                                content: formatContent(hotspot.content || 'Немає додаткової інформації')
                            });
                        };
                    }
                });
            }
        });

        // Налаштування Pannellum з простими опціями
        const pannellumOptions = {
            ...configWithHandlers,
            default: {
                ...dynamicTourConfig.default,
                firstScene: startScene,
            },
            // Прості налаштування для максимальної сумісності
            showZoomCtrl: true,
            showFullscreenCtrl: false,
            autoLoad: true,
            preview: false,
            orientationOnByDefault: false
        };

        try {
            const pannellumViewer = window.pannellum.viewer(panoramaRef.current, pannellumOptions);
            setViewer(pannellumViewer);

            pannellumViewer.on("load", () => {
                setIsLoading(false);
                console.log("Панорама завантажена успішно. Стартова сцена:", startScene);

                // Отладочная информация о hotspots
                const currentSceneConfig = configWithHandlers.scenes[startScene];
                if (currentSceneConfig?.hotSpots) {
                    console.log("Hotspots для сцени:", startScene, currentSceneConfig.hotSpots);
                } else {
                    console.warn("Hotspots не знайдені для сцени:", startScene);
                }
            });

            pannellumViewer.on("error", (err) => {
                setIsLoading(false);
                console.error("Помилка Pannellum:", err);
            });

            pannellumViewer.on("scenechange", (sceneId) => {
                console.log("Перехід до сцени:", sceneId);
                setIsLoading(true);
                setTimeout(() => {
                    setIsLoading(false);
                }, 500);
            });

        } catch (error) {
            console.error('Помилка ініціалізації Pannellum:', error);
            setIsLoading(false);
        }

        // Cleanup
        return () => {
            if (viewer) {
                try {
                    // Очищаємо всі events перед знищенням
                    viewer.off("load");
                    viewer.off("error");
                    viewer.off("scenechange");

                    // Знищуємо viewer
                    viewer.destroy();
                    console.log("Viewer знищено успішно");
                } catch (e) {
                    console.warn('Помилка при очищенні viewer:', e);
                }
            }

            // Очищаємо контейнер від залишків
            if (panoramaRef.current) {
                panoramaRef.current.innerHTML = '';
            }
        };
    }, [currentLab]);

    // Зміна лабораторії
    const handleLabChange = (labId) => {
        // Показуємо індикатор завантаження відразу
        setIsLoading(true);

        // Оновлюємо URL
        const url = new URL(window.location);
        url.searchParams.set("lab", labId);
        window.history.pushState({}, "", url);

        // Невелика затримка перед зміною лабораторії для правильної очистки
        setTimeout(() => {
            setCurrentLab(labId);
        }, 100);
    };

    const currentLabConfig = labConfig[currentLab];
    const currentLabName = currentLabConfig ? currentLabConfig.name : 'Віртуальний тур';

    return (
        <div className="virtual-tour-container">
            {/* Заголовок */}
            <header className="virtual-tour-header">
                <h1>Віртуальний тур по {currentLabName}</h1>
                <p>Використовуйте мишу для навігації, клікайте на точки для переміщення</p>

                {/* Селектор лабораторій */}
                <LabSelector
                    currentLab={currentLab}
                    onLabChange={handleLabChange}
                />
            </header>

            {/* Панорамний переглядач */}
            <div
                ref={panoramaRef}
                className="panorama-viewer"
                style={{ flex: 1, position: 'relative', background: '#000' }}
            />

            {/* Панель керування */}
            <Controls
                onShowHelp={() => setShowModal({
                    show: true,
                    title: 'Як користуватися віртуальним туром',
                    content: `
            <ul>
              <li><strong>Навігація:</strong> Перетягуйте мишею або використовуйте стрілки на клавіатурі, чи використовуйте жести для навігації</li>
              <li><strong>Масштаб:</strong> Колесо миші або клавіші + / - . Чи використовуйте жести для зміни масштабу</li>
              <li><strong>Перехід:</strong> Клікайте на сині знаки переміщення для переходу в іншу частину лабораторії</li>
              <li><strong>Інформація:</strong> Клікайте на сині знаки інформації для отримання додаткових відомостей</li>
            </ul>
          `
                })}
            />

            {/* Індикатор завантаження */}
            {isLoading && (
                <div className="virtual-tour-loading">
                    <div className="spinner"></div>
                    <p>Завантаження...</p>
                </div>
            )}

            {/* Модальне вікно */}
            <Modal
                show={showModal.show}
                title={showModal.title}
                content={showModal.content}
                onClose={() => setShowModal({ show: false, title: '', content: '' })}
            />
        </div>
    );
};

export default VirtualTour;
